/* @flow */
const pubSub = require('./pubsub');

// generates a unique ID
export function newId() {
    return Math.random().toString(36).substr(2, 9);
}

// capitalize/prettify string
export const capitalize = (s: string) => {
    if (typeof s !== 'string') return '';
    let output = s.replace(/([_ ]\w)/g, m => ' ' + m[1].toUpperCase());
    return output.charAt(0).toUpperCase() + output.slice(1);
}

const alias = {
    business: 'Business & Work',
    crime: 'Police & Crime',
    climate: 'Land & Climate',
    taxes: 'Tax Data'
}

const acronymList = [
    /*
        match: substring that should be contained in string to trigger replacement
        regex: what actually gets replaced
        final: final text to replace the regex with
        short: short version of final text to replace with for very long strings
     */
    { match: 'ppsf', regex: 'ppsf', final: 'Price / Sq.Ft.', short: 'PPSF'},
    { match: '_sfr', regex: '_sfr$', final: ' (Single-Family)', short: ' (SFR)' },
    { match: '_mfr', regex: '_mfr$', final: ' (Multi-Family)', short: ' (MFR)' },
    { match: '_th', regex: '_th$', final: ' (Townhome)', short: ' (TH)' },
    { match: '_condo', regex: '_condo$', final: ' (Condo)', short: ' (CND)' },
    { match: 'household_', regex: '^household_', final: 'Household ', short: 'HH ' },
    { match: 'qualified_', regex: 'qualified_', final: 'Qualified ', short: 'Qfd. ' }
]

// change field name to human-friendly format
export const prettyName  = (field: string, charLimit: number = 14): string => {
    /*if (['income', 'rent', 'home_value', 'age'].indexOf(field) !== -1) {
        field = 'Median_' + field;
    } else if (field === 'house_age') {
        field = 'Avg_house_year';
    } else if (field === 'commute_time') {
        field = `Avg_${field}`
    }*/
    if (alias[field]) {
        field = alias[field]
    } else {
        for (let acronym of acronymList) {
            if (field.includes(acronym.match)) {
                const final = field.length - acronym.match.length < charLimit ? acronym.final : acronym.short
                field = field.replace(new RegExp(acronym.regex), final)
            }
        }
    }
    field = field.replace(/:/g, ' - ')
    return capitalize(field);
}

// show notification message
const notifyBox = document.getElementById('notification')
if (notifyBox) {
    function showMsg(message) {
        notifyBox.innerText = message;
        notifyBox.style.display = "block";
        setTimeout(() => {
            notifyBox.style.display = "";
        }, 5000)
    }
    pubSub.subscribe('notify:error', (message) => {
        notifyBox.className = "error-box";
        showMsg(message);
    });
    pubSub.subscribe('notify:info', (message) => {
        notifyBox.className = "info-box";
        showMsg(message);
    });
} else {
    console.error('notifyBox does not exist on page');
}

// show loading message
const loadingBox = document.getElementById('loading-box');
const loadingMsg = document.getElementById('loading-msg');
if (loadingMsg && loadingBox) {
    pubSub.subscribe('notify:startLoad', (message) => {
        loadingMsg.innerText = message;
        loadingBox.style.display = "block";
    });
    pubSub.subscribe('notify:stopLoad', () => {
        loadingBox.style.display = "";
    })
} else {
    console.error('loadingBox or loadingmsg does not exist on page');
}

// correctly sets value in material design lite field
export const setMdlVal = (field: HTMLInputElement, value: string) => {
    // hidden field only (MDL dropdown)
    if (field.type === "hidden") {
        // dropdown, generate fake click event
        ((document.querySelector(`li[data-val="${value}"]`) : any): HTMLElement).click();
    } else {
        // regular text field
        field.value = value;
        ((field.parentElement: any): HTMLElement).classList.add('is-dirty');
    }
}

// deep equality
export function deepEqual(obj1: Object, obj2: Object): boolean {
    if (obj1 == obj2) { // technically the wrong comparison, but we often compare string and number coords
        return true;
    } else if (isObject(obj1) && isObject(obj2)) {
        if (Object.keys(obj1).length !== Object.keys(obj2).length) { return false; }
        for (var prop in obj1) {
            if (!deepEqual(obj1[prop], obj2[prop])) {
                return false;
            }
        }
        return true;
    }
    return false;
}
function isObject(obj) {
    if (typeof obj === "object" && obj != null) {
        return true;
    } else {
        return false;
    }
}

// executes function after the delay has been elapsed, repeated calls prior to delay reset the delay, if "immediate" flag is passed, execute at the leading edge
export function debounce(func:Function, wait:number = 100): Function {
    let timeout;
  
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
    
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

// promise that can be resolved from outside by calling resolve on the object
export function externalPromise() {
	var res, rej;

	var promise = new Promise((resolve, reject) => {
		res = resolve;
		rej = reject;
	});

	promise.resolve = res;
	promise.reject = rej;

	return promise;
}

export const memoize = (callback) => {
    let memo = {}
    console.log('MEMOIZED')
    return (...args) => {
        console.log('ARGS' , args)
        if (memo[args]) {
            return memo[args]
        } else { 
            memo[args] = callback(...args) 
            return memo[args] 
        } 
    }
}

// granule ID mapping logic used by our maps
export const granularity = {
    county: {
        plural: 'counties',
        usId: (d) => parseInt(d.properties.GEOID),
        stateId: (d) => parseInt(d.properties.GEOID),
        // normalizeId: (n) => parseInt(n),
        name: (d) => `${d.properties.NAME} County (${parseInt(d.properties.GEOID)})`,
        usMapPaths: {}
    },
    zip: {
        plural: 'zips',
        usId: (d) => d.properties.z,
        stateId: (d) => d.properties.ZCTA5CE10,
        normalizeId: (n) => `${n}`.padStart(5, "0"),
        name: (d, name) => `${name || '???'} (${d.properties.ZCTA5CE10.padStart(5, "0")})`,
        usMapPaths: {}
    },
    tract: {
        plural: 'tracts',
        stateId: (d) => d.properties.GEOID,
        // normalizeId: (n) => parseInt(n),
        stateFilter: (d) => d.properties.ALAND !== 0,
        name: (d, name) => {
            const idStr = d.properties.GEOID.slice(5, -2) + '.' + d.properties.GEOID.slice(-2)
            const pad = d.properties.NAME.indexOf('.') === -1 ? 4 : 7
            return `${name || '???'} (${d.properties.STATEFP}-${d.properties.COUNTYFP}-${d.properties.NAME.padStart(pad, '0')})`
        }
    }
}